import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, ReceiptText } from 'lucide-react';
import { formatDateBR, type CardsResponse, type InvoiceDetail, type Ym } from '@lira/shared';
import { apiGet } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { CategoryIcon, Modal, MoneyText, Skeleton } from '@/components/ui';
import { PayInvoiceModal } from '@/features/cards/PayInvoiceModal';

interface InvoiceTransactionsModalProps {
  open: boolean;
  card: { id: string; name: string; linkedAccountId: string | null } | null;
  month: Ym;
  startPaying?: boolean;
  onClose: () => void;
}

/** Composição exata e pagamento de uma fatura, sempre carregados pelo motor de cartões. */
export function InvoiceTransactionsModal({ open, card, month, startPaying = false, onClose }: InvoiceTransactionsModalProps) {
  const [paying, setPaying] = useState(false);
  const autoPayStarted = useRef(false);
  const query = useQuery({
    queryKey: ['card-invoice', card?.id, month],
    queryFn: () => apiGet<InvoiceDetail>(`/cards/${card?.id}/invoices/${month}`),
    enabled: open && !!card,
  });
  const cardQuery = useQuery({
    queryKey: ['cards', month],
    queryFn: () => apiGet<CardsResponse>(`/cards?month=${month}`),
    enabled: open && !!card && !card.linkedAccountId,
  });
  const linkedAccountId = card?.linkedAccountId
    ?? cardQuery.data?.cards.find((candidate) => candidate.id === card?.id)?.linkedAccountId
    ?? null;

  useEffect(() => {
    if (!open) {
      autoPayStarted.current = false;
      return;
    }
    if (startPaying && query.data && query.data.remainingCents > 0 && !autoPayStarted.current) {
      autoPayStarted.current = true;
      setPaying(true);
    }
  }, [open, query.data, startPaying]);

  const close = () => {
    setPaying(false);
    onClose();
  };

  return (
    <>
      <Modal open={open && !paying} onClose={close} title={card ? `Fatura ${card.name}` : 'Fatura'} maxWidth={640}>
        {query.isLoading && <div className="space-y-2"><Skeleton className="h-20" /><Skeleton className="h-14" /><Skeleton className="h-14" /></div>}
        {query.isError && <p className="rounded-lg bg-expense-weak px-4 py-5 text-sm text-expense">{errorMessage(query.error)}</p>}
        {query.data && (
          <div className="min-w-0 space-y-4">
            <div className="grid min-w-0 grid-cols-2 gap-2 rounded-xl bg-cards/10 p-3 text-sm sm:grid-cols-3">
              <div className="min-w-0"><p className="text-xs text-muted">Total</p><MoneyText cents={query.data.totalCents} bold className="block truncate" /></div>
              <div className="min-w-0"><p className="text-xs text-muted">Vencimento</p><p className="truncate font-semibold text-ink">{formatDateBR(query.data.dueDate)}</p></div>
              <div className="col-span-2 min-w-0 sm:col-span-1"><p className="text-xs text-muted">Restante</p><MoneyText cents={query.data.remainingCents} bold className="block truncate" /></div>
            </div>
            {query.data.remainingCents > 0 && (
              <button type="button" onClick={() => setPaying(true)} className="inline-flex min-h-11 items-center gap-2 rounded-full bg-cards px-5 text-sm font-semibold uppercase tracking-wide text-white hover:opacity-90">
                <CreditCard className="size-4" />Pagar fatura
              </button>
            )}
            <div>
              <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted"><ReceiptText className="size-4" />Compras</h3>
              {query.data.purchases.length === 0 ? (
                <p className="rounded-lg border border-border px-4 py-6 text-center text-sm text-muted">Nenhuma compra nesta fatura.</p>
              ) : (
                <ul className="overflow-hidden rounded-xl border border-border">
                  {query.data.purchases.map((purchase) => (
                    <li key={purchase.id} className="flex min-w-0 items-center gap-3 border-b border-border px-3 py-3 last:border-0">
                      <CategoryIcon icon={purchase.categoryIcon} color={purchase.categoryColor ?? 'var(--color-muted)'} size={34} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-ink">{purchase.description || purchase.categoryName || 'Compra'}</p>
                        <p className="truncate text-xs text-muted">{formatDateBR(purchase.date)} · {purchase.categoryName ?? 'Sem categoria'}</p>
                      </div>
                      <MoneyText cents={-Math.abs(purchase.amountCents)} colorBySign bold className="shrink-0 text-sm" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="flex items-center gap-2 text-xs text-muted"><CreditCard className="size-4" />Compras de cartão são pagas somente pela fatura.</p>
          </div>
        )}
      </Modal>
      {card && query.data && (
        <PayInvoiceModal
          open={paying}
          onClose={() => {
            setPaying(false);
            if (startPaying) close();
          }}
          cardId={card.id}
          cardName={card.name}
          invoiceMonth={month}
          remainingCents={query.data.remainingCents}
          linkedAccountId={linkedAccountId}
        />
      )}
    </>
  );
}
